import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Form, Input, Space, Typography, message, Grid, Flex } from 'antd';
import PhoneInput from '@tools/components/PhoneInput';
import { CountryCode, E164Number } from 'libphonenumber-js';

import { useAppDispatch, useAppSelector } from '@store/store.tsx';
import {
  initGuestSession,
  markGuestPhoneVerified,
} from '@store/slices/userSlice.tsx';
import { normalizeEmail, normalizePhone } from '@eshop/utils/contactNormalization.ts';
import { useGuestOtp } from '@eshop/hooks/useGuestOtp.ts';
import { CodeInput } from '@tools/components/commonFields.tsx';
import { LoginModal } from '@login/pages/LoginModal.tsx';

const CODE_MIN_LENGTH = 6;

const DEFAULT_BUTTON_RADIUS = '8px';

type GuestIdentifyStepProps = {
  defaultEmail?: string;
  defaultPhone?: string;
  /** @deprecated No longer used after modal-based login */
  checkoutPath?: string;
  blockedReason?: string;
  requiresVerification?: boolean;
};

export const GuestIdentifyStep: React.FC<GuestIdentifyStepProps> = ({
  defaultEmail,
  defaultPhone,
  // checkoutPath is no longer used - login modal handles redirection
  blockedReason,
  requiresVerification = true,
}) => {
  const dispatch = useAppDispatch();
  const guestPhoneVerifiedAt = useAppSelector((s) => s.user.guestPhoneVerifiedAt);
  const radius = DEFAULT_BUTTON_RADIUS;
  const { md } = Grid.useBreakpoint();
  const isMobile = !md;
  const [email, setEmail] = useState<string>('');
  const [phone, setPhone] = useState<E164Number | undefined>();
  const [countryCode, setCountryCode] = useState<CountryCode | undefined>('AR');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [resetCode, setResetCode] = useState(false);
  const [isPersisting, setIsPersisting] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [showExistingUserAlert, setShowExistingUserAlert] = useState(false);

  const {
    status: otpStatus,
    error: otpError,
    cooldown,
    requestCode,
    verifyCode,
    reset: resetOtp,
    canResend,
    isRequesting,
    isVerifying,
    isCodeSent,
    verifiedAt,
    userExists,
    fallbackToken,
  } = useGuestOtp();

  const normalizedDefaultEmail = useMemo(
    () => normalizeEmail(defaultEmail) ?? '',
    [defaultEmail],
  );
  const normalizedDefaultPhone = useMemo(() => normalizePhone(defaultPhone ?? '') ?? '', [defaultPhone]);

  useEffect(() => {
    setEmail(normalizedDefaultEmail);
  }, [normalizedDefaultEmail]);

  useEffect(() => {
    setPhone(normalizedDefaultPhone ? (normalizedDefaultPhone as E164Number) : undefined);
  }, [normalizedDefaultPhone]);

  const isAlreadyVerified = Boolean(guestPhoneVerifiedAt || verifiedAt);
  const isBusy = isRequesting || isVerifying || isPersisting;

  const handleContinueWithoutVerification = async () => {
    const normalizedEmail = validateEmail();
    const normalizedPhone = validatePhone();
    if (!normalizedEmail || !normalizedPhone || !fallbackToken) return;
    setIsPersisting(true);
    try {
      await dispatch(initGuestSession({ email: normalizedEmail, phone: normalizedPhone, fallback_token: fallbackToken })).unwrap();
      message.success('Contacto guardado. Podés continuar con la compra.');
    } catch {
      message.error('No pudimos habilitar la continuación. Volvé a solicitar el código.');
    } finally {
      setIsPersisting(false);
    }
  };

  const validateEmail = useCallback(() => {
    const normalized = normalizeEmail(email) ?? '';
    if (!normalized) {
      setEmailError('Ingresá un email válido');
      return null;
    }
    setEmailError(null);
    return normalized;
  }, [email]);

  const validatePhone = useCallback(() => {
    const phoneValue = typeof phone === 'string' ? phone : '';
    const normalized = normalizePhone(phoneValue);
    if (!normalized) {
      setPhoneError('Ingresá un teléfono válido con prefijo internacional');
      return null;
    }
    setPhoneError(null);
    return normalized;
  }, [phone]);

  const persistGuestInfo = useCallback(
    async (emailValue: string, phoneValue: string, successMessage: string) => {
      setIsPersisting(true);
      try {
        await dispatch(
          initGuestSession({
            email: emailValue,
            phone: phoneValue,
          }),
        ).unwrap();
        message.success(successMessage);
      } catch (error) {
        const detail =
          typeof error === 'string'
            ? error
            : (error as { message?: string })?.message ?? 'No se pudo guardar la información';
        message.error(detail);
      } finally {
        setIsPersisting(false);
      }
    },
    [dispatch],
  );

  const handleSendCode = useCallback(async () => {
    if (requiresVerification && isAlreadyVerified) {
      dispatch(markGuestPhoneVerified(null));
    }
    const normalizedEmail = validateEmail();
    const normalizedPhone = validatePhone();

    if (!normalizedEmail || !normalizedPhone) {
      return;
    }

    if (!requiresVerification) {
      await persistGuestInfo(normalizedEmail, normalizedPhone, 'Información guardada 👍');
      return;
    }

    const result = await requestCode(normalizedPhone, normalizedEmail);
    if (result.success) {
      message.success('Te enviamos un código a tu WhatsApp 📲');
      setResetCode(true);
      setTimeout(() => setResetCode(false), 0);
      // Check if user already exists to show account linking prompt later
      if (result.userExists) {
        setShowExistingUserAlert(true);
      }
    } else {
      if (result.actionCode?.startsWith('login_')) {
        // User already exists and should login
        setShowExistingUserAlert(true);
        setIsLoginModalOpen(true);
        message.info('Ya tenés una cuenta con estos datos. Iniciá sesión para continuar.');
      } else if (result.error) {
        message.error(result.error);
      }
    }
  }, [dispatch, isAlreadyVerified, persistGuestInfo, requestCode, requiresVerification, validateEmail, validatePhone]);

  const handleVerifyCode = useCallback(
    async (value: string) => {
      const code = value.trim();
      if (code.length < CODE_MIN_LENGTH) {
        return;
      }

      const normalizedEmail = validateEmail();
      const normalizedPhone = validatePhone();

      if (!normalizedEmail || !normalizedPhone) {
        setResetCode(true);
        setTimeout(() => setResetCode(false), 0);
        return;
      }

      const verification = await verifyCode(code);
      if (!verification.success) {
        if (verification.error) {
          message.error(verification.error);
        }
        setResetCode(true);
        setTimeout(() => setResetCode(false), 0);
        return;
      }

      const verifiedAtIso = verification.verifiedAt ?? new Date().toISOString();
      dispatch(markGuestPhoneVerified(verifiedAtIso));
      await persistGuestInfo(normalizedEmail, normalizedPhone, 'WhatsApp verificado 👍');
    },
    [dispatch, persistGuestInfo, validateEmail, validatePhone, verifyCode],
  );

  const handlePersistVerified = useCallback(async () => {
    const normalizedEmail = validateEmail();
    const normalizedPhone = validatePhone();
    if (!normalizedEmail || !normalizedPhone) {
      return;
    }
    await persistGuestInfo(normalizedEmail, normalizedPhone, 'Información guardada 👍');
  }, [persistGuestInfo, validateEmail, validatePhone]);

  const handleOpenLoginModal = () => {
    setIsLoginModalOpen(true);
  };

  const handleResend = async () => {
    const normalizedEmail = validateEmail();
    const normalizedPhone = validatePhone();

    if (!normalizedEmail || !normalizedPhone) {
      message.error('Ingresá email y teléfono válidos antes de reenviar el código.');
      return;
    }

    const result = await requestCode(normalizedPhone, normalizedEmail);
    if (result.success) {
      message.success('Código reenviado ✉️');
      setResetCode(true);
      setTimeout(() => setResetCode(false), 0);
    } else if (result.error) {
      message.error(result.error);
    }
  };

  const handleEditContact = useCallback(() => {
    resetOtp();
    dispatch(markGuestPhoneVerified(null));
    setResetCode(true);
    setTimeout(() => setResetCode(false), 0);
  }, [dispatch, resetOtp]);

  useEffect(() => {
    if (otpStatus === 'idle') {
      setResetCode(true);
      setTimeout(() => setResetCode(false), 0);
    }
  }, [otpStatus]);

  useEffect(() => {
    if (!isCodeSent) {
      setResetCode(true);
      setTimeout(() => setResetCode(false), 0);
    }
  }, [isCodeSent]);

  const canPersist = isAlreadyVerified && !isBusy;

  return (
    <Card title="Identificarse" className="guest-identify-step">
      <Space orientation="vertical" size="large" style={{ width: '100%' }}>
        {blockedReason ? (
          <Alert
            type="warning"
            title={blockedReason}
            showIcon
            data-testid="guest-identify-warning"
          />
        ) : null}

        {showExistingUserAlert && userExists && !isAlreadyVerified && (
          <Alert
            type="info"
            showIcon
            title="Ya tenés una cuenta"
            description={
              <Space orientation="vertical" size="small">
                <Typography.Text>
                  Detectamos que ya existe una cuenta con este email o teléfono.
                </Typography.Text>
                <Button type="link" onClick={() => setIsLoginModalOpen(true)} style={{ padding: 0 }}>
                  Iniciá sesión para vincular tu compra
                </Button>
              </Space>
            }
          />
        )}

        <Typography.Paragraph>
          Ingresá tu email y número de WhatsApp para continuar como invitado.
        </Typography.Paragraph>
        <Form layout="vertical">
          <Form.Item
            label="Email"
            htmlFor="guestEmailInput"
            required
            validateStatus={emailError ? 'error' : undefined}
            help={emailError ? <span id="email-error" role="alert">{emailError}</span> : undefined}
          >
            <Input
              id="guestEmailInput"
              placeholder="Ingrese su email"
              autoComplete="email"
              aria-label="Correo electrónico"
              aria-describedby={emailError ? 'email-error' : undefined}
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (emailError) {
                  setEmailError(null);
                }
                dispatch(markGuestPhoneVerified(null));
                if (isCodeSent || fallbackToken) {
                  resetOtp();
                  setResetCode(true);
                  setTimeout(() => setResetCode(false), 0);
                }
              }}
              onBlur={validateEmail}
              disabled={isBusy}
            />
          </Form.Item>

          <Form.Item
            label="WhatsApp"
            htmlFor="guestPhoneInput"
            required
            validateStatus={phoneError ? 'error' : undefined}
            help={phoneError ? <span id="phone-error" role="alert">{phoneError}</span> : undefined}
          >
            <Flex gap="small" vertical={isMobile} align="start">
              <PhoneInput
                id="guestPhoneInput"
                name="guestPhoneInput"
                placeholder="Celular"
                aria-label="Número de WhatsApp"
                aria-describedby={phoneError ? 'phone-error' : undefined}
                value={phone}
                onCountryChange={setCountryCode}
                onChange={(value) => {
                  setPhone(value ?? undefined);
                  if (phoneError) {
                    setPhoneError(null);
                  }
                  dispatch(markGuestPhoneVerified(null));
                  if (isCodeSent || fallbackToken) {
                    resetOtp();
                    setResetCode(true);
                    setTimeout(() => setResetCode(false), 0);
                  }
                }}
                defaultCountry={(countryCode ?? 'AR') as CountryCode}
                disabled={isBusy}
                style={{ flex: 1, width: isMobile ? '100%' : undefined }}
              />
              {(!requiresVerification || (!isCodeSent && !isAlreadyVerified)) && (
                <Button
                  onClick={handleSendCode}
                  disabled={isBusy || Boolean(phoneError) || Boolean(emailError)}
                  size="small"
                  className="wsp-code-btn"
                  shape="default"
                  style={{ borderRadius: radius, fontWeight: 500, ...(isMobile ? { alignSelf: 'flex-end' } : {}) }}
                  loading={isRequesting || isPersisting}
                >
                  {requiresVerification ? 'Enviar código' : 'Continuar'}
                </Button>
              )}
            </Flex>
          </Form.Item>
        </Form>

        {!isCodeSent && otpError ? (
          <Typography.Text type="danger">{otpError}</Typography.Text>
        ) : null}
        {fallbackToken && !isAlreadyVerified && (
          <Button onClick={handleContinueWithoutVerification} loading={isPersisting} disabled={isBusy}>
            Continuar sin verificar
          </Button>
        )}

        {isCodeSent && !isAlreadyVerified && (
          <Space orientation="vertical" style={{ width: '100%' }} size="middle">
            <Typography.Paragraph>
              Ingresá el código de validación que recibiste por WhatsApp.
            </Typography.Paragraph>
            <CodeInput
              digits={CODE_MIN_LENGTH}
              onComplete={handleVerifyCode}
              digitBox={{ style: { marginRight: 5, width: 45 } }}
              resetCode={resetCode}
              setResetCode={setResetCode}
              disabled={isBusy}
              aria-label="Código de verificación OTP"
              aria-describedby={otpError ? 'otp-error' : undefined}
            />
            {/* Persistent verification feedback */}
            {isVerifying && (
              <span className="otp-feedback otp-feedback--pending" role="status">
                Verificando código...
              </span>
            )}
            {otpError ? <span id="otp-error" role="alert" className="otp-feedback otp-feedback--error">{otpError}</span> : null}
            <Space size="small">
              <Button
                type="link"
                onClick={handleResend}
                disabled={!canResend || isRequesting || isVerifying}
              >
                {cooldown > 0 ? `Reenviar código (${cooldown})` : 'Reenviar código'}
              </Button>
              <Button
                type="text"
                onClick={handleEditContact}
                disabled={isBusy}
                style={{ padding: 0 }}
              >
                Editar datos
              </Button>
            </Space>
          </Space>
        )}

        {isAlreadyVerified && userExists === false && (
          <Alert
            type="success"
            showIcon
            title="WhatsApp verificado"
            description={
              <Space orientation="vertical" size="small">
                <Typography.Text>
                  Podés continuar como invitado o crear una cuenta para guardar tus datos.
                </Typography.Text>
                <Button type="link" onClick={() => setIsLoginModalOpen(true)} style={{ padding: 0 }}>
                  Crear cuenta (opcional)
                </Button>
              </Space>
            }
          />
        )}

        <Space className="w-100" style={{ justifyContent: 'space-between' }}>
          <Button type="link" onClick={handleOpenLoginModal} disabled={isBusy}>
            ¿Ya tenés cuenta? Ingresá
          </Button>
          <Button
            type="primary"
            onClick={handlePersistVerified}
            disabled={!canPersist}
            loading={isPersisting}
            shape="default"
            style={{ borderRadius: radius }}
          >
            Guardar y continuar
          </Button>
        </Space>

        <LoginModal open={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      </Space>
    </Card>
  );
};
